import React, { useState, useEffect } from 'react';
import { useSipWebsocket } from 'app/protocol/SipWebsocketProvider';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel, Select, MenuItem, ListItemText, Checkbox, FormControlLabel } from '@material-ui/core';
import { useInterval } from 'react-timers-hooks';
import { Link, useHistory } from 'react-router-dom';
// import StartRecordingCommand from 'app/protocol/recording/StartRecordingCommand';
// import StopRecordingCommand from 'app/protocol/recording/StopRecordingCommand';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import DeviceEditService from '../service/DeviceEditService';
import deviceControl from '../service/DeviceService';

const colorNames = [
	'Blue', 'Red', 'Yellow', 'Green', 'Purple',
	'Orange', 'Cyan', 'Magenta', 'Lime', 'Pink',
	'Teal', 'Lavender', 'Brown', 'Beige', 'Maroon',
	'Mint', 'Olive', 'Coral', 'Navy', 'Grey'
];

function StreamingDialog(props) {
	// const { sipId, sendMessage } = useSipWebsocket();
	// const [open, setOpen] = useState(false);
	// const [mode, setMode] = useState('recording');
	// const [actionMessage, setActionMessage] = useState('Recording');
	// const [deviceExternalId, setDeviceExternalId] = useState(null);
	// const history = useHistory();

	// const handleStop = () => {
	// 	if (sipId) sendMessage(new StopRecordingCommand(sipId, deviceExternalId));
	// 	MeasurementEditService.stopRecordingSubmitted(props.measurementId);
	// 	setMode('waiting');
	// };
	const { message } = useSipWebsocket();
	const [streamingData, setStreamingData] = useState([]);
	const [selectedUnits, setSelectedUnits] = useState([]);
	const [streamingInProgress, setStreamingInProgress] = useState(false);
	const [uom, setUom] = useState(null);
	const [deviceData, setDeviceData] = useState({});
	const [selectedSensor, setSelectedSensor] = useState('');
	const [unitMapping, setUnitMapping] = useState([]);
	const [showAllInOneChart, setShowAllInOneChart] = useState(true);
	const [loadingData, setLoadingData] = useState(false);
	const [sensorName, setSensorName] = useState('');

	useEffect(() => {
		onOpen();
	}, []);

	useEffect(() => {
		if (message && streamingInProgress && message.type !== 'PONG') {
			const formattedData = message.payload.measurementData.data.map(data => ({
				...data,
				time: formatTime(data.time)
			}));
			setStreamingData(prevData => {
				const newData = [...prevData, ...formattedData];
				const newMapping = deviceData.deviceType?.sensorTypeList?.flatMap(sensorType =>
					sensorType.valueList.map(value => {
						const unit = newData.find(d => d.vid === value.externalId)?.unit;
						return unit ? { name: value.name, unit } : null;
					})
				).filter(Boolean);
				setUnitMapping(newMapping);
				return newData;
			});
		}
	}, [message, streamingInProgress, deviceData]);

	useEffect(() => {
		if (streamingInProgress && uom) {
			console.log('Streaming in progress and UOM is set. Ready to display chart.');
		}
	}, [streamingInProgress, uom]);

	useEffect(() => {
		deviceControl.findById(props.item.id).then(data => {
			const mapping = data.deviceType.sensorTypeList.flatMap(sensorType =>
				sensorType.valueList.map(value => {
					const unit = streamingData.find(d => d.vid === value.externalId)?.unit;
					return unit ? { name: value.name, unit } : null;
				})
			).filter(Boolean);

			setDeviceData(data);
			setUnitMapping(mapping);

		});
	}, [props.item.id]);

	const formatTime = (timeString) => {
		const [date, time] = timeString.split(' ');
		const [year, month, day] = date.split('/');
		const [hours, minutes, seconds] = time.split(':');
		return `${year}.${month}.${day} ${hours}:${minutes}:${seconds.split('.')[0]}`;
	};

	const onOpen = () => {
		setStreamingData([]);
		setSelectedUnits([]);
		setSelectedSensor('');
		setUnitMapping([]);
		setShowAllInOneChart(true);
		setLoadingData(true);

		deviceControl.findById(props.item.id).then(data => {
			setDeviceData(data);

			// Set the first sensor as the selected sensor
			const firstSensor = data.deviceType.sensorTypeList[0];
			if (firstSensor) {
				setSelectedSensor(firstSensor.externalId);
				setSensorName(firstSensor.name);
				const allUnits = firstSensor.valueList.map(value => value.name);
				setSelectedUnits(allUnits);
			}

			DeviceEditService.startStreaming(props.item.id).then(resp => {
				setStreamingInProgress(true);
			});
		});
	};

	const onClose = () => {
		DeviceEditService.stop(props.item.id).then(resp => {
			setStreamingInProgress(false);
			setStreamingData([]);
			setSelectedUnits([]);
			setSelectedSensor('');
			setUnitMapping([]);
			props.onClose();
		});
	};

	const handleSensorChange = (event) => {
		const sensorId = event.target.value;
		if (sensorId !== selectedSensor) {
			setSelectedSensor(sensorId);
			setLoadingData(true);
			setStreamingData([]);

			const selectedSensorData = deviceData.deviceType?.sensorTypeList.find(sensor => sensor.externalId === sensorId);
			if (selectedSensorData) {
				setSensorName(selectedSensorData.name);
				const updatedUnitMapping = selectedSensorData.valueList.map(value => {
					const unit = streamingData.find(d => d.vid === value.externalId)?.unit;
					return unit ? { name: value.name, unit } : null;
				}).filter(Boolean);
				setUnitMapping(updatedUnitMapping);
				setSelectedUnits([]);
			}
		}
	};

	const handleUnitChange = (event) => {
		setSelectedUnits(event.target.value);
	};

	const handleShowAllChange = (event) => {
		setShowAllInOneChart(event.target.checked);
	};

	const renderLineChart = (unit) => {
		let filteredData;
		if (unit === 'All Data') {
			filteredData = streamingData;
		} else {
			const actualUnit = unitMapping.find(u => u.name === unit)?.unit;
			filteredData = streamingData.filter(data => data.unit === actualUnit);
		}

		// Create a lookup for sensor names based on sensor IDs
		const sensorNameLookup = {};
		deviceData.deviceType.sensorTypeList.forEach(sensor => {
			sensorNameLookup[sensor.externalId] = sensor.name;
		});

		// Create a mapping of time to values for each unit including sensor name and vid
		const dataMap = {};
		filteredData.forEach(data => {
			if (!dataMap[data.time]) {
				dataMap[data.time] = {};
			}
			const sensorValueName = `${data.unit}-${data.sid}-${data.vid}`;
			dataMap[data.time][sensorValueName] = data.val;
		});

		// Ensure all keys exist for each timestamp
		const allKeys = new Set();
		filteredData.forEach(data => {
			const sensorValueName = `${data.unit}-${data.sid}-${data.vid}`;
			allKeys.add(sensorValueName);
		});

		Object.keys(dataMap).forEach(time => {
			allKeys.forEach(key => {
				if (!Object.prototype.hasOwnProperty.call(dataMap[time], key)) {
					dataMap[time][key] = null;
				}
			});
		});

		// Convert the dataMap back to an array format for the chart
		const chartData = Object.keys(dataMap).map(time => ({
			time,
			...dataMap[time],
		}));

		const valueTypes = Object.keys(chartData[0]).filter(key => key !== 'time');

		return (
			<div key={unit} style={{
				borderRadius: '15px',
				boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
				padding: '20px',
				backgroundColor: '#fff',
				border: '1px solid #ddd',
				display: 'flex',
				justifyContent: 'center',
				alignItems: 'center',
				marginBottom: '20px',
			}}>
				<ResponsiveContainer width="100%" height={400}>
					<LineChart data={chartData}>
						<CartesianGrid strokeDasharray="3 3" />
						<XAxis dataKey="time" />
						<YAxis
							domain={[0, dataMax => Math.max(10, dataMax * 1.1)]}
							tickFormatter={(value) => Math.round(value)}
						/>
						<Tooltip />
						<Legend />
						{valueTypes.map((valueType, index) => {
							const [unitName, sensorId, valueId] = valueType.split('-');
							const sensorDisplayName = sensorNameLookup[sensorId] || `Sensor ${sensorId}`;
							return (
								<Line
									key={valueType}
									type="monotone"
									dataKey={valueType}
									stroke={colorNames[index % colorNames.length]}
									name={`${unitName} (${sensorDisplayName})`}
									dot={{ r: 4, stroke: colorNames[index % colorNames.length] }}
									activeDot={{ r: 6 }}
									isAnimationActive={false}
									connectNulls
								/>
							);
						})}
					</LineChart>
				</ResponsiveContainer>
			</div>
		);
	};


	useEffect(() => {
		if (streamingData.length > 0) {
			const sensorDataAvailable = streamingData.some(data => data.sid === selectedSensor);
			if (sensorDataAvailable) {
				setLoadingData(false);
			}
		}
	}, [streamingData, selectedSensor]);

	return (
		<Dialog open fullWidth maxWidth="md" style={{ borderRadius: '20px', overflow: 'hidden' }}>

			<div /*id="form-dialog-title"*/ className="w-full flex items-center justify-between px-32 py-8">

				<div >Streaming</div>

				<div className="flex gap-16 items-center py-10">
					<FormControl variant="outlined" margin="normal" className='m-0' style={{ width: '200px' }}>
						<Select
							labelId="sensor-select-label"
							className='m-0'
							value={selectedSensor}
							onChange={handleSensorChange}
							style={{ width: '100%' }}
						>
							{deviceData.deviceType?.sensorTypeList.map((sensor, index) => (
								<MenuItem key={`${sensor.externalId}-${index}`} value={sensor.externalId}>
									{sensor.name}
								</MenuItem>
							))}
						</Select>
					</FormControl>

					{selectedSensor && (
						<FormControl variant="outlined" margin="normal" className='m-0' style={{ width: '200px' }}>
							<Select
								labelId="unit-select-label"
								className='m-0'
								multiple
								value={selectedUnits}
								onChange={handleUnitChange}
								renderValue={(selected) => selected.join(', ')}
								style={{ width: '100%' }}
							>
								{Array.isArray(unitMapping) && unitMapping.map((unit, index) => (
									<MenuItem key={`${unit.name}-${index}`} value={unit.name}>
										<Checkbox checked={selectedUnits.indexOf(unit.name) > -1} />
										<ListItemText primary={unit.name} />
									</MenuItem>
								))}
							</Select>
						</FormControl>
					)}

					<FormControlLabel
						control={
							<Checkbox
								checked={showAllInOneChart}
								onChange={handleShowAllChange}
								color="primary"
							/>
						}
						label="Combined"
					/>
				</div>
			</div>

			<DialogContent style={{ padding: '30px', backgroundColor: '#fafafa' }}>
				{loadingData ? (
					<div>Loading data for {sensorName}...</div>
				) : streamingData.length === 0 ? (
					<div>No data available. Please select a sensor and wait for new data.</div>
				) : showAllInOneChart ? (
					renderLineChart('All Data')
				) : (
					selectedUnits.map(unit => renderLineChart(unit))
				)}
			</DialogContent>
			<DialogActions style={{ backgroundColor: '#f5f5f5', borderTop: '1px solid #ddd' }}>
				<Button onClick={onClose} style={{ color: '#007bff' }}>Close</Button>
			</DialogActions>
		</Dialog>
	);
}

export default StreamingDialog;
